/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useState } from "react";

interface ReportMonsterModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string; // The public link to the reported monster; submitted with the report (not shown).
  hasImage: boolean; // whether the monster has an image (if not, 'image'/'both' aren't offered)
  errorOnly?: boolean; // catalog monsters: only 'incorrect data' reports are allowed (no violation/copyright)
  presetName?: string;  // a logged-in reporter's display name (pre-filled)
  presetEmail?: string; // a logged-in reporter's email (pre-filled)
  lockIdentity?: boolean; // when true (logged in), name/email are pre-filled and read-only
  onSubmit: (report: {
    reportType: 'violation' | 'error' | 'copyright';
    reporterName: string;
    reporterEmail: string;
    offensiveType?: 'text' | 'image' | 'both'; // sent for 'violation' / 'copyright' reports
    description: string;
  }) => Promise<boolean>;
}

const labelStyle: React.CSSProperties = { display: 'block', margin: '10px 0', fontWeight: 'bold' };
const inputStyle: React.CSSProperties = { display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '4px', padding: '6px' };

// Form shown when a visitor reports another user's monster. Handles three report types: a 'violation'
// (inappropriate content), a 'copyright' (copyright infringement) — both of which collect which part
// is at issue (text/image/both) — and an 'error' (incorrect data, e.g. a wrong CR or a typo).
// Collects who they are and why, then submits to the reportMonster Cloud Function via onSubmit. The
// monster link is submitted with the report but not shown.
const ReportMonsterModal: React.FC<ReportMonsterModalProps> = ({ isOpen, onClose, url, hasImage, errorOnly, presetName, presetEmail, lockIdentity, onSubmit }) => {
  const [reportType, setReportType] = useState<'violation' | 'error' | 'copyright'>(errorOnly ? 'error' : 'violation');
  const [reporterName, setReporterName] = useState<string>('');
  const [reporterEmail, setReporterEmail] = useState<string>('');
  const [offensiveType, setOffensiveType] = useState<'text' | 'image' | 'both'>('text');
  const [description, setDescription] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // The modal stays mounted (returns null) when closed; reset the form each time it reopens.
  useEffect(() => {
    setReportType(errorOnly ? 'error' : 'violation');
    setReporterName(presetName || '');
    setReporterEmail(presetEmail || '');
    setOffensiveType('text');
    setDescription('');
    setSubmitting(false);
    setSubmitted(false);
    setError('');
  }, [isOpen, url, errorOnly, presetName, presetEmail]);

  if (!isOpen) return null;

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reporterEmail);
  const canSubmit = reporterName.trim() !== '' && emailValid && description.trim() !== '' && !submitting;
  // Violations and copyright reports identify which part is at issue; error reports don't.
  const hasPart = reportType !== 'error';

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    setError('');
    const ok = await onSubmit({
      reportType,
      reporterName: reporterName.trim(),
      reporterEmail: reporterEmail.trim(),
      offensiveType: hasPart ? (hasImage ? offensiveType : 'text') : undefined,
      description: description.trim()
    });
    setSubmitting(false);
    if (ok) {
      setSubmitted(true);
    } else {
      setError('Sorry, we could not submit your report. Please try again.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ width: '500px', height: 'auto', minHeight: '300px' }} onClick={(e) => e.stopPropagation()}>
        <span className="modal-title" style={{ display: 'block', textAlign: 'center' }}>Report a Monster</span>
        {submitted ? (
          <div>
            <p>Thank you. Your report has been submitted and will be reviewed.</p>
            <div className="modal-actions" style={{ textAlign: 'center' }}>
              <button type="button" onClick={onClose}>Close</button>
            </div>
          </div>
        ) : (
          <div>
            {!errorOnly &&
              // Catalog (built-in) monsters have no owner to act on, so only data-error feedback is
              // accepted — not inappropriate-content or copyright reports.
              <div style={{ margin: '10px 0', fontWeight: 'bold' }}>
                What kind of report is this?<br/>
                <label style={{ marginLeft: '16px', fontWeight: 'normal' }}>
                  <input type="radio" name="reportType" checked={reportType === 'violation'} onChange={() => setReportType('violation')} /> Inappropriate content
                </label>
                <label style={{ marginLeft: '16px', fontWeight: 'normal' }}>
                  <input type="radio" name="reportType" checked={reportType === 'copyright'} onChange={() => setReportType('copyright')} /> Copyright violation
                </label>
                <label style={{ marginLeft: '16px', fontWeight: 'normal' }}>
                  <input type="radio" name="reportType" checked={reportType === 'error'} onChange={() => setReportType('error')} /> Incorrect data
                </label>
              </div>
            }
            <label style={labelStyle}>Your name:
              <input type="text" value={reporterName} onChange={(e) => setReporterName(e.target.value)}
                     readOnly={lockIdentity}
                     style={lockIdentity ? { ...inputStyle, background: '#e8e8e8' } : inputStyle} />
            </label>
            <label style={labelStyle}>Your email:
              <input type="email" value={reporterEmail} onChange={(e) => setReporterEmail(e.target.value)}
                     readOnly={lockIdentity}
                     style={lockIdentity ? { ...inputStyle, background: '#e8e8e8' } : inputStyle} />
            </label>
            {/* Only ask which part (text/image/both) when there's an image to point at; otherwise the
                offensive/copyrighted part is necessarily the text (submitted as 'text'). */}
            {hasPart && hasImage && (
              <div style={{ margin: '10px 0', fontWeight: 'bold' }}>
                {reportType === 'copyright' ? 'Which part is copyrighted?' : 'What did you find offensive?'}
                <label style={{ marginLeft: '16px', fontWeight: 'normal' }}>
                  <input type="radio" name="offensiveType" checked={offensiveType === 'text'} onChange={() => setOffensiveType('text')} /> The text
                </label>
                <label style={{ marginLeft: '16px', fontWeight: 'normal' }}>
                  <input type="radio" name="offensiveType" checked={offensiveType === 'image'} onChange={() => setOffensiveType('image')} /> The image
                </label>
                <label style={{ marginLeft: '16px', fontWeight: 'normal' }}>
                  <input type="radio" name="offensiveType" checked={offensiveType === 'both'} onChange={() => setOffensiveType('both')} /> Both
                </label>
              </div>
            )}
            <label style={labelStyle}>{
              reportType === 'copyright' ? 'Describe the copyright violation (what work, and who owns it?)'
              : reportType === 'error' ? 'What is incorrect, and what should it be?'
              : 'Why do you find this offensive?'
            }
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} style={{ ...inputStyle, resize: 'vertical' }} />
            </label>
            {error && <p style={{ color: 'darkred', fontWeight: 'bold' }}>{error}</p>}
            <div className="modal-actions" style={{ textAlign: 'center' }}>
              <button type="button" onClick={handleSubmit} disabled={!canSubmit}>{submitting ? 'Submitting…' : 'Submit Report'}</button>
              <button type="button" onClick={onClose}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportMonsterModal;
